// AIBlogGenerator.jsx
// ─────────────────────────────────────────────────────────────────────────────
// This page no longer has any manual "generate" controls.
// The AI automatically generates articles every 48h via useNotifications.js.
// This component just displays those auto-generated articles in a clean reader.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';
import AppNav from './AppNav';

function renderContent(text, isMobile) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={i} style={{ fontFamily: 'var(--font-head)', fontSize: isMobile ? 18 : 22, fontWeight: 800, color: '#0a2e14', margin: '2rem 0 0.75rem', letterSpacing: '-0.02em', lineHeight: 1.2, borderBottom: '2px solid #d4a017', paddingBottom: '0.5rem' }}>
          {line.replace('## ', '')}
        </h2>
      );
    } else if (line.startsWith('> ')) {
      elements.push(
        <blockquote key={i} style={{ margin: '1.5rem 0', padding: '1rem 1.5rem', borderLeft: '4px solid #d4a017', background: 'rgba(212,160,23,0.06)', fontStyle: 'italic', fontSize: isMobile ? 14 : 16, color: '#3a3a2a', lineHeight: 1.8 }}>
          {line.replace('> ', '')}
        </blockquote>
      );
    } else if (line.startsWith('- ') || line.startsWith('• ')) {
      const items = [];
      while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('• '))) {
        const raw = lines[i].replace(/^[-•] /, '');
        items.push(<li key={i} style={{ marginBottom: 6, color: '#2a2a1a', lineHeight: 1.75 }}>{parseBold(raw)}</li>);
        i++;
      }
      elements.push(<ul key={`ul-${i}`} style={{ margin: '1rem 0 1rem 1.25rem', padding: 0, fontSize: isMobile ? 13.5 : 15 }}>{items}</ul>);
      continue;
    } else if (line.trim() === '') {
      elements.push(<div key={i} style={{ height: '0.75rem' }} />);
    } else if (line.trim()) {
      elements.push(
        <p key={i} style={{ fontSize: isMobile ? 13.5 : 15, color: '#2a2a1a', lineHeight: 1.85, margin: '0 0 0.5rem' }}>
          {parseBold(line)}
        </p>
      );
    }
    i++;
  }
  return elements;
}

function parseBold(text) {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1
      ? <strong key={i} style={{ color: '#14532d', fontWeight: 700 }}>{part}</strong>
      : part
  );
}

export default function AIBlogGenerator({
  priceMode = 'student',
  onModeToggle,
  onNavigate,
  user = null,
  onUserChange,
  notifications = [],
  unseenCount = 0,
  onMarkAllSeen,
  onNotificationClick,
  formatRelative,
  aiArticles = [],
}) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [selectedArticle, setSelectedArticle] = useState(null);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // Auto-select the latest article if there's only one or none selected
  useEffect(() => {
    if (aiArticles.length > 0 && !selectedArticle) {
      setSelectedArticle(aiArticles[0]);
    }
  }, [aiArticles, selectedArticle]);

  const navH = isMobile ? 60 : 72;

  const navProps = {
    priceMode, onModeToggle, onNavigate, user, onUserChange,
    notifications, unseenCount, onMarkAllSeen, onNotificationClick, formatRelative,
    isMobile, activePage: 'blog',
  };

  // ── Empty state — no articles generated yet ──────────────────────────────
  if (aiArticles.length === 0) {
    return (
      <div style={{ background: '#fcf9f8', minHeight: '100vh', fontFamily: 'var(--font-body)' }}>
        <AppNav {...navProps} />
        <div style={{ height: navH }} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: '2rem', textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(10,46,20,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20, fontSize: 28 }}>✦</div>
          <h2 style={{ fontFamily: 'var(--font-head)', fontSize: 22, fontWeight: 800, color: '#0a2e14', marginBottom: 10 }}>AI is preparing your first article</h2>
          <p style={{ fontSize: 14, color: '#64748b', maxWidth: 380, lineHeight: 1.7, marginBottom: 24 }}>
            NaijaAgro's AI research desk automatically writes market intelligence articles every 48 hours. Check back shortly — your first post will appear here.
          </p>
          <button
            onClick={() => onNavigate && onNavigate('blog')}
            style={{ background: '#0a2e14', color: '#fff', border: 'none', padding: '11px 22px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-head)' }}
          >
            ← Back to Blog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: '#fcf9f8', minHeight: '100vh', fontFamily: 'var(--font-body)' }}>
      <style>{`
        @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        .art-card:hover { border-color: rgba(212,160,23,0.5) !important; background: rgba(212,160,23,0.03) !important; }
      `}</style>

      <AppNav {...navProps} />
      <div style={{ height: navH }} />

      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #0a2e14 0%, #051a0b 100%)', padding: isMobile ? '2rem 1.25rem 1.75rem' : '2.5rem 2rem 2rem', borderBottom: '3px solid #d4a017' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(212,160,23,0.12)', border: '1px solid rgba(212,160,23,0.3)', borderRadius: 20, padding: '4px 12px', marginBottom: 12 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#4ade80', boxShadow: '0 0 6px #4ade80', display: 'inline-block' }} />
            <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#d4a017', fontFamily: 'var(--font-head)' }}>AI Research Desk · Auto-Generated</span>
          </div>
          <h1 style={{ fontFamily: 'var(--font-head)', fontSize: isMobile ? 'clamp(1.4rem,6vw,2rem)' : '2.2rem', fontWeight: 900, color: '#fff', letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: 10 }}>
            NaijaAgro Market Intelligence
          </h1>
          <p style={{ fontSize: isMobile ? 13 : 14.5, color: 'rgba(255,255,255,0.55)', lineHeight: 1.65, maxWidth: 520 }}>
            In-depth articles on Nigerian food prices, market trends, and agricultural economics — written automatically by our AI research desk every 48 hours.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: isMobile ? '1.25rem 1rem' : '2rem 2rem' }}>

        {/* ── Article list sidebar + reader layout ── */}
        {!selectedArticle ? (
          // Grid of all articles
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16, animation: 'fadeUp 0.4s ease' }}>
            {aiArticles.map(article => (
              <button
                key={article.id}
                className="art-card"
                onClick={() => setSelectedArticle(article)}
                style={{
                  textAlign: 'left', cursor: 'pointer',
                  background: '#fff', border: '1.5px solid rgba(10,46,20,0.1)',
                  borderRadius: 12, padding: '1.25rem', transition: 'all 0.18s',
                  display: 'flex', flexDirection: 'column', gap: 8,
                }}
              >
                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#d4a017', fontFamily: 'var(--font-head)' }}>{article.category}</span>
                <h3 style={{ fontFamily: 'var(--font-head)', fontSize: 15, fontWeight: 800, color: '#0a2e14', lineHeight: 1.3, margin: 0 }}>{article.title}</h3>
                <p style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.6, margin: 0, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>{article.excerpt}</p>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                  <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'var(--font-head)' }}>{article.date} · {article.readTime}</span>
                  <span style={{ fontSize: 11, color: '#52b788', fontWeight: 600 }}>✦ AI</span>
                </div>
              </button>
            ))}
          </div>
        ) : (
          // Full article reader
          <div style={{ animation: 'fadeUp 0.4s ease' }}>
            {/* Back + article list nav */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
              <button
                onClick={() => setSelectedArticle(null)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: '1.5px solid rgba(10,46,20,0.15)', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: '#334155', fontFamily: 'var(--font-head)' }}
              >
                ← All Articles
              </button>
              <button
                onClick={() => onNavigate && onNavigate('blog')}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', border: '1.5px solid rgba(10,46,20,0.15)', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: '#334155', fontFamily: 'var(--font-head)' }}
              >
                ← Blog Home
              </button>
            </div>

            {/* Article header card */}
            <div style={{ background: 'linear-gradient(135deg, #0a2e14, #051a0b)', padding: isMobile ? '1.5rem 1.25rem' : '2rem 2.5rem', borderRadius: 12, marginBottom: 24, borderBottom: '3px solid #d4a017' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', padding: '4px 10px', background: 'rgba(212,160,23,0.15)', color: '#d4a017', border: '1px solid rgba(212,160,23,0.3)', fontFamily: 'var(--font-head)', borderRadius: 4 }}>{selectedArticle.category}</span>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>{selectedArticle.date} · {selectedArticle.readTime}</span>
                <span style={{ marginLeft: 'auto', fontSize: 11, background: 'rgba(82,183,136,0.15)', color: '#52b788', padding: '3px 10px', borderRadius: 20, fontWeight: 600 }}>✦ AI Generated</span>
              </div>
              <h1 style={{ fontFamily: 'var(--font-head)', fontSize: isMobile ? 'clamp(1.2rem,5.5vw,1.7rem)' : 'clamp(1.4rem,2.5vw,2.1rem)', fontWeight: 900, color: '#fff', letterSpacing: '-0.03em', lineHeight: 1.12, marginBottom: 12 }}>
                {selectedArticle.title}
              </h1>
              <p style={{ fontSize: isMobile ? 13 : 14.5, color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, margin: 0 }}>{selectedArticle.excerpt}</p>
              <div style={{ marginTop: 14, fontSize: 11.5, color: 'rgba(255,255,255,0.35)', fontFamily: 'var(--font-head)', fontWeight: 600 }}>
                By NaijaAgro AI Research Desk
              </div>
            </div>

            {/* Article body */}
            <article className="article-body" style={{ background: '#fff', border: '1px solid rgba(10,46,20,0.07)', borderRadius: 12, padding: isMobile ? '1.25rem 1rem' : '2.5rem 3rem', marginBottom: 24 }}>
              <div style={{ padding: isMobile ? '1rem' : '1.25rem 1.5rem', background: '#f8f6f3', borderLeft: '4px solid #d4a017', borderRadius: '0 8px 8px 0', marginBottom: '2rem' }}>
                <p style={{ fontSize: isMobile ? 14 : 15, color: '#424841', lineHeight: 1.8, margin: 0, fontStyle: 'italic' }}>{selectedArticle.excerpt}</p>
              </div>
              {renderContent(selectedArticle.content, isMobile)}
            </article>

            {/* Other articles */}
            {aiArticles.length > 1 && (
              <div style={{ marginTop: 32 }}>
                <h3 style={{ fontFamily: 'var(--font-head)', fontSize: 14, fontWeight: 700, color: '#0a2e14', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 14 }}>More from the AI Research Desk</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {aiArticles.filter(a => a.id !== selectedArticle.id).slice(0, 4).map(article => (
                    <button
                      key={article.id}
                      className="art-card"
                      onClick={() => { setSelectedArticle(article); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      style={{ textAlign: 'left', cursor: 'pointer', background: '#fff', border: '1.5px solid rgba(10,46,20,0.1)', borderRadius: 10, padding: '12px 16px', transition: 'all 0.18s', display: 'flex', gap: 12, alignItems: 'flex-start' }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#d4a017', fontFamily: 'var(--font-head)' }}>{article.category}</span>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0a2e14', lineHeight: 1.3, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{article.title}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{article.date} · {article.readTime}</div>
                      </div>
                      <span style={{ fontSize: 18, color: '#d4a017', flexShrink: 0, marginTop: 2 }}>→</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div style={{ background: 'linear-gradient(135deg, #0a2e14 0%, #051a0b 100%)', padding: isMobile ? '1.5rem' : '2rem', borderRadius: 10, borderTop: '3px solid #d4a017', marginTop: 32 }}>
              <p style={{ fontSize: isMobile ? 13.5 : 15, color: 'rgba(255,255,255,0.8)', lineHeight: 1.65, marginBottom: 16 }}>
                Check current and predicted prices for 30+ agricultural commodities across Nigeria.
              </p>
              <button
                onClick={() => onNavigate && onNavigate('predict')}
                style={{ background: '#d4a017', color: '#001805', border: 'none', padding: '11px 22px', cursor: 'pointer', fontFamily: 'var(--font-head)', fontSize: 13, fontWeight: 700, borderRadius: 6 }}
              >
                Get Price Predictions →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer style={{ background: 'linear-gradient(135deg, #0a2e14 0%, #051a0b 100%)', color: '#fff', padding: '1.75rem 2rem', borderTop: '3px solid #d4a017', marginTop: 32 }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 12 }}>
          <div style={{ fontFamily: 'var(--font-head)', fontWeight: 800, fontSize: 15, color: '#d4a017' }}>NaijaAgro Predict</div>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
            {['home', 'blog', 'about', 'terms'].map(l => (
              <button key={l} onClick={() => onNavigate && onNavigate(l)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'rgba(255,255,255,0.6)', fontFamily: 'var(--font-body)', padding: 0, textTransform: 'capitalize' }}>{l}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>© 2026 NaijaAgro Predict. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}